import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClubBook } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { useClub } from "../pages/club/ClubLayout";
import { reading } from "@pagemates/shared/copy";

const txt = reading.startBook;

/*
 * "Start reading" for a book on deck. Starting always takes one
 * confirmation, so a stray tap never changes the club's book. When the club
 * is mid-book, only an admin can start another (the current one is finished
 * first, dated today, in the same call, so the shelf never has two current
 * books); a member whose turn it is is told they pick next, once the club
 * finishes the current one.
 */
export function useStartBook(clubId: string, onDone?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, finishCurrent }: { id: string; finishCurrent: boolean }) => api.startBook(clubId, id, finishCurrent),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["books", clubId] }); await qc.invalidateQueries({ queryKey: ["suggestions", clubId] }); onDone?.(); },
  });
}

/** `onDone` runs once the book has started; `onCancel` when the question is waved off. */
export function StartBook({ clubId, book, current, size = "sm", menuItem, ask = false, onDone, onCancel, onOpen }:
  { clubId: string; book: ClubBook; current: ClubBook | null; size?: "sm" | ""; menuItem?: boolean; ask?: boolean; onDone?: () => void; onCancel?: () => void; onOpen?: () => void }) {
  const { club } = useClub();
  const isAdmin = club.data ? club.data.myRole !== "member" : false;
  const start = useStartBook(clubId, onDone);
  const [asking, setAsking] = useState(ask);
  // Mid-book, a member can't start another; say what happens instead.
  const waiting = Boolean(current) && !isAdmin;
  const go = () => { if (onOpen) onOpen(); else setAsking(true); }; // a menu item hands the question to its row

  if (waiting) {
    if (menuItem) return null;
    return <p className="small muted start-wait">{txt.youPickNextOnce}<i>{current!.book.title}</i>.</p>;
  }
  if (asking) {
    return (
      <div className="handoff">
        {current ? (
          <>
            <b>{txt.finish}<i>{current.book.title}</i> first?</b>
            <p className="small muted">{txt.itGoesOnThe}<i>{book.book.title}</i>{txt.startsRatingsAndThe}</p>
          </>
        ) : (
          <>
            <b>{txt.start}<i>{book.book.title}</i>{txt.forTheClub}</b>
            <p className="small muted">{txt.itBecomesTheBook}</p>
          </>
        )}
        <div className="row" style={{ gap: 8 }}>
          <button type="button" className="btn primary sm" disabled={start.isPending} onClick={() => start.mutate({ id: book.id, finishCurrent: Boolean(current) })}>
            {start.isPending ? txt.working : current ? txt.finishAndStart : txt.startIt}
          </button>
          <button type="button" className="btn ghost sm" onClick={() => { setAsking(false); onCancel?.(); }}>{txt.cancel}</button>
        </div>
        {start.isError && <p className="error small">{describeError(start.error)}</p>}
      </div>
    );
  }
  if (menuItem) return <button role="menuitem" disabled={start.isPending} onClick={go}>{txt.startReading}</button>;
  return <button type="button" className={`btn primary ${size}`.trim()} disabled={start.isPending} onClick={go}>{txt.startReading}</button>;
}
